import { Card } from './Card';
import { useMyAppointmentsLocale } from '../pages/MyAppointments';
import {
  UpcomingAppoinment,
  UpcomingAppointmentsDayFilter,
} from '../interfaces';
import React, {
  createContext,
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  AppointmentTypeEnum,
  UpcomingAppointmentCounts,
  useGetAppointmentsByPatientCodexIdQuery,
  useGetAppointmentsByProviderQuery,
} from 'graphql/generated/remote-schema-hasura';

import {
  FHIR_APPOINTMENT_STATUS,
  ORDER_BY_DIRECTION_NUMERIC,
  SORT_FHIR_APPOINTMENT_BY,
} from 'utilities/interfaces';
import { useAuth } from 'auth/context/AuthContext';
import { SIGNAL_CHANNELS, useChannelSignal } from 'hooks/useChannelSignal';
import { Roles } from '../../../firebase/interfaces';
import { QueryContextType } from 'app/my-appointments/components/QueryContextType';
import Loader from '../../../components/loaderComponent';
import { AppointmentHasuraStatus } from '../const/appointment';
import { AppointmentPayment } from '../../dashboard/components/upcomingAppointments/interfaces/upcomingAppointments.interfaces';
import UpcomingAppointmentCard from './upcoming-appointments/UpcomingAppointmentCard';
import { Counter } from './CounterBadge';
import PaginateBlock from '../../../components/PaginateBlock';
import CancelAppointmentModal from './modalContent/CancelAppointmentModal';

const INITIAL_USER_DATE = new Date(Date.now() - 30 * 60 * 1000).toISOString();

const offsetParams = 'upcomingAppointmentsOffset';

export const upcomingStatuses = [
  FHIR_APPOINTMENT_STATUS.PROPOSED,
  FHIR_APPOINTMENT_STATUS.BOOKED,
  FHIR_APPOINTMENT_STATUS.PENDING,
  FHIR_APPOINTMENT_STATUS.WAITLIST,
];

type dataType = {
  appointments: UpcomingAppoinment[];
  counts: UpcomingAppointmentCounts;
  total: number;
};
type varsType = {
  limit: number;
  offset: number;
  dayFilter: UpcomingAppointmentsDayFilter;
};

export interface UpcomingAppointmentsContextType
  extends QueryContextType<UpcomingAppoinment, varsType> {
  loading: boolean;
  data: UpcomingAppoinment[];
  counts: UpcomingAppointmentCounts;
  total: number;
  variables: {
    limit: number;
    offset: number;
    dayFilter: UpcomingAppointmentsDayFilter;
  };
  refetch: unknown;
  setVars: React.Dispatch<React.SetStateAction<varsType>>;
  allAmount?: number;
}

const UpcomingAppoinmentsContext =
  createContext<UpcomingAppointmentsContextType>({
    loading: false,
    appointments: [],
  } as unknown as UpcomingAppointmentsContextType);

export const useUpcomingAppointments = () => {
  const context = useContext(UpcomingAppoinmentsContext);
  if (!context?.setVars && process.env.REACT_APP_IS_DEBUG_MODE) {
    throw new Error(
      'useUpcomingAppointments must be used within a NotificationsProvider.',
    );
  }
  return context;
};

export const upcomingAppointmentsOffset = 'upcomingAppointmentsOffset';

const UpcomingPatientsAppointmentsProvider: React.FC<{
  children: React.ReactNode;
  patientCodexId?: string | undefined;
  offset: number;
}> = ({ children, offset }) => {
  const { user: loggedUser } = useAuth();
  const [data, setData] = useState<dataType>({
    appointments: [],
    counts: { today: 0, tomorrow: 0, week: 0, month: 0 },
    total: 0,
  });
  const [vars, setVars] = useState<varsType>({
    limit: 3,
    offset: offset || 0,
    dayFilter: UpcomingAppointmentsDayFilter.NOFILTER,
  });

  const { loading, refetch } = useGetAppointmentsByPatientCodexIdQuery({
    variables: {
      appointmentStatus: upcomingStatuses,
      limit: vars.limit,
      offset: offset || vars.offset,
      from: INITIAL_USER_DATE,
      sort: SORT_FHIR_APPOINTMENT_BY.SUMMARY_DATE,
      sortDirection: ORDER_BY_DIRECTION_NUMERIC.ASC,
      dayFilter:
        vars.dayFilter === UpcomingAppointmentsDayFilter.NOFILTER
          ? undefined
          : vars.dayFilter,
    },
    context: {
      ignoreGlobalErrors: true,
    },
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      if (data.getFHIRAppointmentByRequestPatientCodexId.total > 0) {
        const allUpcommingAppointments: UpcomingAppoinment[] =
          data.getFHIRAppointmentByRequestPatientCodexId.appointment.map(
            (appointment) => ({
              appointmentId: appointment.appointmentCodexId,
              date: appointment.start || '',
              end: appointment.end || '',
              paymentSessionOpenedAt: appointment.paymentSessionOpenedAt || '',
              patientId: loggedUser?.uuid || '',
              providerId: appointment.codexProviderId || '',
              subjectOfAppointment: appointment?.subjectOfAppointment || '',
              lastProcessedDate: appointment?.created || '',
              appointmentUrl: appointment?.appointmentUrl || '',
              appointmentType:
                appointment.appointmentType || AppointmentTypeEnum.Chat,
              name: `${appointment.providerFirstName} ${appointment.providerLastName}`,
              status: appointment.status as FHIR_APPOINTMENT_STATUS,
              paymentInformation:
                appointment?.paymentInformation as AppointmentPayment,
              appointmentStatus:
                appointment.appointmentStatus as AppointmentHasuraStatus,
              pendingRescheduleRequest: appointment.pendingRescheduleRequest,
            }),
          );
        setData({
          appointments: allUpcommingAppointments,
          counts: data.getFHIRAppointmentByRequestPatientCodexId.counts,
          total: data.getFHIRAppointmentByRequestPatientCodexId.total,
        });
      } else {
        setData({
          appointments: [],
          counts: data.getFHIRAppointmentByRequestPatientCodexId.counts,
          total: 0,
        });
      }
    },
  });

  useChannelSignal(
    (first: boolean) => {
      if (!first) {
        refetch();
      }
    },
    SIGNAL_CHANNELS.APPOINTMENTS,
    loggedUser?.uuid,
  );

  const value: UpcomingAppointmentsContextType = {
    data: data.appointments,
    counts: data.counts,
    variables: vars,
    total: data.total,
    refetch,
    loading: loading,
    setVars,
  };

  return (
    <UpcomingAppoinmentsContext.Provider value={value}>
      {children}
    </UpcomingAppoinmentsContext.Provider>
  );
};

const UpcomingProviderAppointmentsProvider: React.FC<{
  children: React.ReactNode;
  patientCodexId?: string | undefined;
  offset?: number | undefined;
}> = ({ children, patientCodexId, offset }) => {
  const { user: loggedUser } = useAuth();

  const [data, setData] = useState<dataType>({
    appointments: [],
    counts: { today: 0, tomorrow: 0, week: 0, month: 0 },
    total: 0,
  });
  const [vars, setVars] = useState<varsType>({
    limit: 3,
    offset: offset || 0,
    dayFilter: UpcomingAppointmentsDayFilter.NOFILTER,
  });

  const { loading, refetch } = useGetAppointmentsByProviderQuery({
    skip: !(loggedUser?.mfaPhoneNumber || loggedUser?.phoneNumber),
    variables: {
      patientCodexId: patientCodexId,
      appointmentStatus: upcomingStatuses,
      limit: vars.limit,
      offset: vars.offset,
      from: INITIAL_USER_DATE,
      sort: SORT_FHIR_APPOINTMENT_BY.SUMMARY_DATE,
      sortDirection: ORDER_BY_DIRECTION_NUMERIC.ASC,
      dayFilter:
        vars.dayFilter === UpcomingAppointmentsDayFilter.NOFILTER
          ? undefined
          : vars.dayFilter,
    },
    context: {
      ignoreGlobalErrors: true,
    },
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      if (data.getFHIRAppointmentByRequestProviderId.total > 0) {
        const allUpcommingAppointments: UpcomingAppoinment[] =
          data.getFHIRAppointmentByRequestProviderId.appointment.map(
            (appointment) => ({
              appointmentId: appointment.appointmentCodexId,
              date: new Date(appointment.start || '').toISOString(),
              end: new Date(appointment.end || '').toISOString(),
              patientId: appointment.codexPatientId,
              providerId: appointment.codexProviderId || '',
              appointmentType:
                appointment.appointmentType || AppointmentTypeEnum.Chat,
              name: `${appointment.patientFirstName} ${appointment.patientLastName}`,
              status: appointment.status as FHIR_APPOINTMENT_STATUS,
              appointmentStatus:
                appointment.appointmentStatus as AppointmentHasuraStatus,
              paymentInformation:
                appointment?.paymentInformation as AppointmentPayment,
              subjectOfAppointment: appointment?.subjectOfAppointment || '',
              pendingRescheduleRequest: appointment?.pendingRescheduleRequest,
            }),
          );
        setData({
          appointments: allUpcommingAppointments,
          counts: data.getFHIRAppointmentByRequestProviderId.counts,
          total: data.getFHIRAppointmentByRequestProviderId.total,
        });
      } else {
        setData({
          appointments: [],
          counts: data.getFHIRAppointmentByRequestProviderId.counts,
          total: 0,
        });
      }
    },
  });

  useChannelSignal(
    (first: boolean) => {
      if (!first) {
        refetch();
      }
    },
    SIGNAL_CHANNELS.APPOINTMENTS,
    loggedUser?.uuid,
  );

  const value: UpcomingAppointmentsContextType = {
    data: data.appointments,
    counts: data.counts,
    variables: vars,
    total: data.total,
    refetch,
    loading: loading,
    setVars,
  };

  return (
    <UpcomingAppoinmentsContext.Provider value={value}>
      {children}
    </UpcomingAppoinmentsContext.Provider>
  );
};

export const UpcomingAppointmentsProvider: React.FC<{
  children: React.ReactNode;
  patientCodexId?: string | undefined;
}> = ({ children, patientCodexId }) => {
  const { user } = useAuth();
  const queryParams = new URLSearchParams(location.search);
  const offset = queryParams.get(upcomingAppointmentsOffset) ?? 0;
  const userRole = user?.role;

  const UpcomingAppointmentsProvider =
    userRole === Roles.PROVIDER
      ? UpcomingProviderAppointmentsProvider
      : UpcomingPatientsAppointmentsProvider;

  return (
    <UpcomingAppointmentsProvider
      patientCodexId={patientCodexId}
      offset={Number(offset)}
    >
      {children}
    </UpcomingAppointmentsProvider>
  );
};

export const UpcomingAppointments: React.FC<{
  patientCodexId?: string | undefined;
  setHasUpcomingAppointments?: Dispatch<SetStateAction<boolean>>;
}> = ({ patientCodexId, setHasUpcomingAppointments }) => {
  return (
    <UpcomingAppointmentsProvider patientCodexId={patientCodexId}>
      <div data-tour="upcoming-appointments" className="w-full">
        <UpcomingAppointmentsRenderer
          setHasUpcomingAppointments={setHasUpcomingAppointments}
        />
      </div>
    </UpcomingAppointmentsProvider>
  );
};

const UpcomingAppointmentsRenderer: React.FC<{
  setHasUpcomingAppointments?: Dispatch<SetStateAction<boolean>>;
}> = ({ setHasUpcomingAppointments }) => {
  const { loading, locale } = useMyAppointmentsLocale();

  const {
    data: upcomingAppointments,
    loading: dataLoading,
    total,
    setVars,
    variables,
  } = useUpcomingAppointments();

  const hasShowPaginate = useMemo(
    () => total > variables.limit,
    [total, variables.limit],
  );

  const paginateSubtitle = useMemo(
    () =>
      `${locale?.showing} ${variables.offset + 1}-${Math.min(
        variables.offset + variables.limit,
        total,
      )} ${locale?.appointments} ${locale?.from} ${total}`,
    [locale, total, variables],
  );

  const setNewOffset = (offset: number) =>
    setVars((vars) => ({ ...vars, offset }));

  useEffect(() => {
    if (!total) return;
    setHasUpcomingAppointments?.(!!total);
  }, [setHasUpcomingAppointments, total]);

  if (!locale || loading || dataLoading) {
    return <Loader />;
  }

  return upcomingAppointments?.length ? (
    <Card
      testId="upcoming-appointments-card"
      title={locale.upcomingAppointmentsCardTitle}
      alwaysExpanded
      rightComponentWrap={false}
      rightComponent={
        <div className="desktop:hidden">
          <Counter total={total} />
        </div>
      }
      subtitle={
        <div className="hidden desktop:block">
          <Counter total={total} />
        </div>
      }
      titleClassName="text-xl desktop:text-h3 text-neutral-800  font-semibold"
    >
      {dataLoading ? (
        <div data-testid="upcoming-appointments-loading">
          <Loader />
        </div>
      ) : (
        upcomingAppointments.map((appointment) => (
          <UpcomingAppointmentCard
            appointment={appointment}
            key={appointment.appointmentId}
          />
        ))
      )}

      {hasShowPaginate && !dataLoading && (
        <div className="flex w-full max-desktop:flex-col gap-4 justify-between items-center mt-7">
          <p className="text-sm font-medium text-med-gray-3">
            {paginateSubtitle}
          </p>
          <PaginateBlock
            itemsPerPage={variables.limit}
            totalItems={total}
            setNewOffset={setNewOffset}
            offsetParams={offsetParams}
          />
        </div>
      )}

      <CancelAppointmentModal />
    </Card>
  ) : null;
};
